<template>
  <div class="warehouse-page">
    <p v-if="error" class="banner error">{{ error }}</p>
    <p v-if="notice" class="banner ok">{{ notice }}</p>

    <div class="mode-tabs">
      <button
        v-for="mode in modes"
        :key="mode.key"
        type="button"
        class="mode-tab"
        :class="{ active: activeMode === mode.key }"
        @click="setMode(mode.key)"
      >
        {{ mode.label }}
      </button>
    </div>

    <div class="toolbar">
      <input
        v-model="filters.search"
        type="search"
        placeholder="Search SKU, item, doc no, customer..."
        @keyup.enter="search"
      >
      <button class="btn" type="button" @click="search" :disabled="exporting">
        <i class="fa-solid fa-magnifying-glass"></i> Search
      </button>
      <input v-model="filters.from" type="date" :disabled="filters.status === 'pending'" @change="search">
      <input v-model="filters.to" type="date" :disabled="filters.status === 'pending'" @change="search">
      <select v-model="filters.location" @change="search">
        <option value="all">All locations</option>
        <option v-for="location in lookups.locations" :key="location" :value="location">{{ location }}</option>
      </select>
      <select v-model="filters.group" @change="search">
        <option value="all">All groups</option>
        <option v-for="group in lookups.groups || []" :key="group.key" :value="group.key">{{ group.label }}</option>
      </select>
      <select v-model="filters.category" @change="search">
        <option value="all">All categories</option>
        <option v-for="category in lookups.categories || []" :key="category" :value="category">{{ category }}</option>
      </select>
      <button class="btn ghost" type="button" @click="reload" :disabled="exporting">Refresh</button>
      <button class="btn export" type="button" @click="exportCsv" :disabled="exporting || !total">
        <i class="fa-solid fa-file-export"></i> {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <p v-if="filters.status === 'pending'" class="hint">
      Pending shows all currently open sales-order quantity (not limited by date).
    </p>

    <div class="table-card">
      <table>
        <thead>
          <tr>
            <th class="col-no">No</th>
            <th>Type</th>
            <th>Doc</th>
            <th>Date</th>
            <th>Customer</th>
            <th>SKU</th>
            <th>Item</th>
            <th>Location</th>
            <th>Group</th>
            <th class="col-num">Qty</th>
            <th class="col-num">Open</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id">
            <td class="col-no">{{ rowNumber(index) }}</td>
            <td>{{ row.docType === 'delivery' ? 'DO' : 'Order' }}</td>
            <td>{{ row.docNum }}</td>
            <td>{{ row.docDate }}</td>
            <td>{{ row.cardName || row.cardCode || '—' }}</td>
            <td>{{ row.sku }}</td>
            <td>{{ row.itemName || '—' }}</td>
            <td>{{ row.location }}</td>
            <td>{{ row.locationGroupLabel || '—' }}</td>
            <td class="col-num">{{ number(row.quantity) }}</td>
            <td class="col-num">{{ row.docType === 'order' ? number(row.openQuantity) : '—' }}</td>
            <td>
              <span class="status-pill" :class="row.fulfillmentStatus">{{ row.fulfillmentStatusLabel }}</span>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="12" class="empty">No orders or delivery notes for these filters. Pull from SAP B1 on Data Flow.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pager">
      <label class="page-size">
        Rows
        <select v-model.number="pageSize">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <span>{{ total }} records</span>
      <button type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
      <span>Page {{ page }}</span>
      <button type="button" :disabled="page * pageSize >= total" @click="page += 1">Next</button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import warehouseApi, { downloadWarehouseCsv } from '@/utils/warehouseApi'

function isoDate(date) {
  return date.toISOString().slice(0, 10)
}

const route = useRoute()
const router = useRouter()

const start = new Date()
start.setMonth(start.getMonth() - 6)

const error = ref('')
const notice = ref('')
const exporting = ref(false)
const page = ref(1)
const pageSize = ref(20)
const total = ref(0)
const rows = ref([])
const lookups = ref({ sources: [], locations: [], categories: [], groups: [] })
const filters = reactive({
  search: '',
  source: 'all',
  location: 'all',
  group: 'all',
  category: 'all',
  type: 'all',
  status: 'all',
  from: isoDate(start),
  to: isoDate(new Date())
})

const modes = [
  { key: 'all', label: 'All', type: 'all', status: 'all' },
  { key: 'orders', label: 'Orders', type: 'order', status: 'all' },
  { key: 'deliveries', label: 'Delivery notes (DO)', type: 'delivery', status: 'all' },
  { key: 'pending', label: 'Pending', type: 'order', status: 'pending' }
]

const activeMode = computed(() => {
  if (filters.status === 'pending') return 'pending'
  if (filters.type === 'order') return 'orders'
  if (filters.type === 'delivery') return 'deliveries'
  return 'all'
})

function number(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })
}

function rowNumber(index) {
  return (page.value - 1) * pageSize.value + index + 1
}

function applyRouteQuery() {
  if (route.query.type) filters.type = String(route.query.type)
  if (route.query.status) filters.status = String(route.query.status)
  if (route.query.search) filters.search = String(route.query.search)
  if (route.query.location) filters.location = String(route.query.location)
  if (route.query.group) filters.group = String(route.query.group)
  if (route.query.category) filters.category = String(route.query.category)
  if (route.query.from) filters.from = String(route.query.from)
  if (route.query.to) filters.to = String(route.query.to)
}

function setMode(key) {
  const mode = modes.find((entry) => entry.key === key) || modes[0]
  filters.type = mode.type
  filters.status = mode.status
  page.value = 1
  syncQuery()
  reload()
}

function syncQuery() {
  const query = {
    type: filters.type !== 'all' ? filters.type : undefined,
    status: filters.status !== 'all' ? filters.status : undefined
  }
  router.replace({ query: Object.fromEntries(Object.entries(query).filter(([, v]) => v != null)) })
}

async function reload() {
  try {
    error.value = ''
    notice.value = ''
    const { data } = await warehouseApi.get('/api/warehouse/fulfillment', {
      params: { ...filters, page: page.value, pageSize: pageSize.value }
    })
    rows.value = data.rows || []
    total.value = data.total || 0
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load orders and delivery notes.'
  }
}

function search() {
  if (page.value !== 1) page.value = 1
  else reload()
}

async function exportCsv() {
  try {
    exporting.value = true
    error.value = ''
    await downloadWarehouseCsv('/api/warehouse/export/fulfillment', { ...filters }, 'warehouse-orders-do.csv')
    notice.value = 'CSV export downloaded.'
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to export CSV.'
  } finally {
    exporting.value = false
  }
}

watch(page, reload)
watch(pageSize, () => {
  if (page.value !== 1) page.value = 1
  else reload()
})

onMounted(async () => {
  applyRouteQuery()
  const { data } = await warehouseApi.get('/api/warehouse/lookups')
  lookups.value = data
  await reload()
})
</script>

<style scoped>
.mode-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.mode-tab {
  padding: 7px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.78rem;
  cursor: pointer;
}

.mode-tab.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
  color: white;
}

.toolbar, .pager {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar input, .toolbar select, .btn, .pager button, .page-size select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.813rem;
}

.toolbar input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}

.btn {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
  cursor: pointer;
}

.btn.ghost {
  background: white;
  color: #475569;
}

.btn.export {
  background: #059669;
  border-color: #059669;
}

.btn i {
  margin-right: 4px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hint {
  margin: -4px 0 12px;
  font-size: 0.75rem;
  color: #64748b;
}

.banner {
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.813rem;
}

.banner.error {
  background: #fef2f2;
  color: #b91c1c;
}

.banner.ok {
  background: #ecfdf5;
  color: #047857;
}

.table-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 10px 8px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  text-align: left;
}

th {
  background: #f8fafc;
  color: #475569;
}

.col-no {
  width: 44px;
  text-align: center;
  white-space: nowrap;
}

.col-num {
  text-align: right;
  white-space: nowrap;
}

.empty {
  text-align: center;
  color: #64748b;
  padding: 24px;
}

.status-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.status-pill.pending {
  background: #ffedd5;
  color: #9a3412;
}

.status-pill.fulfilled {
  background: #dcfce7;
  color: #166534;
}

.status-pill.ordered {
  background: #e0f2fe;
  color: #0369a1;
}

.pager {
  justify-content: flex-end;
  color: #64748b;
  font-size: 0.813rem;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: auto;
}
</style>
